import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import {
    Button,
    Row,
    Col,
    Collapse,
    Card,
    CardBody,
    Table,
    Badge,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { SERVER_URL } from '../../../ServerLink'; // adjust path if needed

const MediaViewerModal = ({ isOpen, toggle, mediaUrl, mediaType, title }) => (
    <div className={`modal ${isOpen ? 'd-block' : 'd-none'}`} style={{ backgroundColor: 'rgba(0,0,0,0.9)' }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content bg-dark text-white border-0">
                <div className="modal-header border-0 pb-2">
                    <h5 className="modal-title">{title || 'Media Preview'}</h5>
                    <button type="button" className="btn-close btn-close-white" onClick={toggle}></button>
                </div>
                <div className="modal-body text-center py-5">
                    {mediaType?.startsWith('video/') ? (
                        <video
                            controls
                            style={{
                                maxHeight: '85vh',
                                maxWidth: '100%',
                                borderRadius: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                            }}
                        >
                            <source src={mediaUrl} type={mediaType} />
                            Your browser does not support the video tag.
                        </video>
                    ) : (
                        <img
                            src={mediaUrl}
                            alt={title}
                            style={{
                                maxHeight: '85vh',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                borderRadius: '12px',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                            }}
                        />
                    )}
                </div>
                <div className="modal-footer border-0 justify-content-center">
                    <Button color="secondary" onClick={toggle}>Close</Button>
                </div>
            </div>
        </div>
    </div>
);

const InspectionItemRowPreview = ({
    item,
    section,
    signedUrlCache,
    getMediaDisplayUrl,
    setSelectedMedia,
}) => {
    const attachments = item.attachments || [];
    const needsWork = item.needs_work === true;
    const status = needsWork
        ? item.completed
            ? <span className="badge bg-success text-white">Done</span>
            : <span className="badge bg-warning text-dark">Pending</span>
        : <span className="badge bg-secondary text-white">N/A</span>;

    const handleMediaClick = async (attachment) => {
        const url = await getMediaDisplayUrl(attachment);
        if (url && !url.includes('placeholder')) {
            const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(attachment);
            const mediaType = isVideo ? 'video/mp4' : 'image/jpeg';
            setSelectedMedia({ url, title: item.name, type: mediaType });
        }
    };

    const renderThumbnail = useCallback((file, cacheKey, idx) => {
        const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(file);
        let src = signedUrlCache[cacheKey] || signedUrlCache[file];

        if (!src) {
            src = 'https://via.placeholder.com/60?text=Loading...';
        }

        return (
            <div key={`${section}-${idx}`} className="position-relative">
                {isVideo ? (
                    <div
                        className="rounded shadow-sm d-flex align-items-center justify-content-center"
                        style={{
                            width: 60,
                            height: 60,
                            backgroundColor: '#000',
                            cursor: 'pointer',
                            border: '2px solid #3b82f6',
                        }}
                        onClick={() => handleMediaClick(file)}
                    >
                        <FeatherIcon icon="play-circle" size={32} className="text-white" />
                    </div>
                ) : (
                    <img
                        src={src}
                        alt={item.name}
                        className="rounded shadow-sm"
                        style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            border: '2px solid #3b82f6',
                        }}
                        onClick={() => handleMediaClick(file)}
                    />
                )}
            </div>
        );
    }, [signedUrlCache, handleMediaClick, section, item.name]);

    return (
        <tr className="align-middle">
            <td className="fw-semibold px-4 py-3" style={{ fontSize: '0.9rem' }}>
                <div className="d-flex align-items-center">
                    <FeatherIcon icon="check-square" size={16} className="me-2 text-muted" />
                    {item.name || 'Unnamed Item'}
                </div>
            </td>
            <td className="text-center px-3 py-3">
                <div className="py-1 fw-medium">
                    {needsWork ? 'Yes' : 'No'}
                </div>
            </td>
            <td className="text-center px-3 py-3">
                {item.due_date ? new Date(item.due_date).toLocaleDateString() : '—'}
            </td>
            <td className="text-center px-3 py-3">
                {status}
            </td>
            <td className="px-3 py-3">
                <p className="mb-0 text-muted">{item.comments || 'No comments'}</p>
            </td>
            <td className="text-center px-2 py-3">
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {attachments.map((att, i) => {
                        const key = `att-${att}-${i}`;
                        return renderThumbnail(att, key, i);
                    })}
                    {attachments.length === 0 && <small className="text-muted">No media</small>}
                </div>
            </td>
            <td className="text-center px-3 py-3">
                <div className="py-1 fw-medium">
                    {item.memos?.length || 0} memo{item.memos?.length !== 1 ? 's' : ''}
                </div>
            </td>
        </tr>
    );
};

const BuildingInspectionUI = ({ inspectionData }) => {
    const { token } = useParams();
    const [selectedMedia, setSelectedMedia] = useState({ url: '', title: '', type: '' });
    const [openSections, setOpenSections] = useState([]);
    const [signedUrlCache, setSignedUrlCache] = useState({});

    const toggleSection = (idx) => {
        setOpenSections(prev => prev.includes(idx) ? prev.filter(s => s !== idx) : [...prev, idx]);
    };

    const getMediaKey = useCallback((attachment) => {
        if (typeof attachment !== 'string') return null;
        try {
            const url = new URL(attachment);
            return url.pathname.replace(/^\/+/, '');
        } catch {
            return attachment;
        }
    }, []);

    const getMultipleSignedUrls = async (keys) => {
        if (!Array.isArray(keys) || keys.length === 0) return {};

        try {
            const response = await fetch(
                `${SERVER_URL}/v2/building-inspections/public/signed-urls`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ keys }),
                }
            );

            if (!response.ok) throw new Error("Failed to generate signed URLs");

            const result = await response.json();
            return result.data || {};
        } catch (error) {
            console.error("Signed URLs fetch failed:", error);
            return {};
        }
    };

    const getMediaDisplayUrl = useCallback(
        async (attachment) => {
            const key = getMediaKey(attachment);
            if (!key) return "https://via.placeholder.com/60?text=Invalid";

            if (signedUrlCache[key]) return signedUrlCache[key];

            try {
                const urlMap = await getMultipleSignedUrls([key]);
                const signed = urlMap[key];

                if (signed) {
                    setSignedUrlCache(prev => ({ ...prev, [key]: signed }));
                    return signed;
                }
            } catch (err) {
                console.error("Signed URL fetch failed:", err);
            }

            return "https://via.placeholder.com/60?text=Error";
        },
        [signedUrlCache, getMediaKey]
    );

    useEffect(() => {
        const keys = [];
        Object.values(inspectionData || {})
            .filter(Array.isArray)
            .flatMap(arr => arr.flatMap(item => item.attachments || []))
            .filter(Boolean)
            .forEach(att => {
                const k = getMediaKey(att);
                if (k) keys.push(k);
            });

        if (keys.length > 0) {
            getMultipleSignedUrls([...new Set(keys)]).then(urlMap => {
                setSignedUrlCache(urlMap);
            }).catch(console.error);
        }
    }, [inspectionData, getMediaKey]);

    const sections = [
        { key: 'interior', title: 'INTERIOR AREAS' },
        { key: 'elevators', title: 'ELEVATORS' },
        { key: 'underground', title: 'UNDERGROUND AREAS' },
        { key: 'outside', title: 'EXTERIOR & GROUNDS' },
        { key: 'misc_others', title: 'MISC / OTHERS' },
    ];

    const property = inspectionData?.property_details?.property_id || {};
    const inspector = inspectionData?.completed_by?.supervisedByName || 'Unknown';
    const inspectionDate = inspectionData?.inspection_date
        ? new Date(inspectionData.inspection_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
        : 'Not provided';

    return (
        <>
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ backgroundColor: '#0D568F', minHeight: '64px' }}>
                <h5 className="mb-0 text-white fw-normal d-flex align-items-center">
                    <FeatherIcon icon="eye" size={22} className="me-2" />
                    Building Inspection Report (Preview)
                </h5>
                <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
            </div>

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa' }}>
                {/* Property Info */}
                <Card className="shadow-sm border-0 mb-4">
                    <CardBody>
                        <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
                            <FeatherIcon icon="map-pin" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                            Property Information
                        </div>
                        <Row className="g-4">
                            <Col md={6}>
                                <strong>Property Title:</strong><br />
                                {property.title || 'Not provided'}
                            </Col>
                            <Col md={6}>
                                <strong>Address / Building:</strong><br />
                                {property.address || inspectionData?.property_details?.building_name || 'Not provided'}
                            </Col>
                            <Col md={6}>
                                <strong>Inspection Date:</strong><br />
                                {inspectionDate}
                            </Col>
                            <Col md={6}>
                                <strong>Completed By:</strong><br />
                                {inspector}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Inspection Items per Section */}
                {sections.map(({ key, title }, idx) => {
                    const items = inspectionData?.[key] || [];
                    const isOpen = openSections.includes(idx);
                    const totalMedia = items.reduce((sum, item) => sum + (item.attachments?.length || 0), 0);

                    return (
                        <Card key={key} className="mb-4 shadow-sm border-0 overflow-hidden">
                            <div
                                className="d-flex align-items-center justify-content-between p-4 text-primary bg-light"
                                onClick={() => toggleSection(idx)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center fw-semibold" style={{ fontSize: '0.95rem' }}>
                                    <FeatherIcon icon="layers" size={18} strokeWidth={2.5} className="me-2" />
                                    {title}
                                </div>
                                <div className="d-flex align-items-center gap-4">
                                    {items.length} items
                                    {totalMedia > 0 && (
                                        <div className="d-flex align-items-center gap-2 ps-2 border-start border-secondary-subtle">
                                            <FeatherIcon icon="image" size={16} className="text-muted" />
                                            <span className="text-muted">{totalMedia} media</span>
                                        </div>
                                    )}
                                    <FeatherIcon icon={isOpen ? 'chevron-up' : 'chevron-down'} size={24} className="text-primary ms-auto" />
                                </div>
                            </div>

                            <Collapse isOpen={isOpen}>
                                <CardBody className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="table-bordered mb-0">
                                            <thead className="bg-light border-bottom border-2">
                                                <tr>
                                                    <th className="text-muted fw-semibold text-uppercase text-start">Category</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Needs Work</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Due Date</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Status</th>
                                                    <th className="text-muted fw-semibold text-uppercase">Notes</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Attachments</th>
                                                    <th className="text-muted fw-semibold text-uppercase text-center">Memos</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <Fragment key={i}>
                                                        <InspectionItemRowPreview
                                                            item={item}
                                                            section={key}
                                                            signedUrlCache={signedUrlCache}
                                                            getMediaDisplayUrl={getMediaDisplayUrl}
                                                            setSelectedMedia={setSelectedMedia}
                                                        />
                                                        <tr><td colSpan={7} className="p-0 border-bottom" style={{ opacity: 0.25 }} /></tr>
                                                    </Fragment>
                                                ))}
                                                {items.length === 0 && (
                                                    <tr>
                                                        <td colSpan={7} className="text-center py-4 text-muted">
                                                            No items recorded in this section
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Collapse>
                        </Card>
                    );
                })}

                {/* Conclusions & Recommendations */}
                <Card className="shadow-sm border-0 mb-5">
                    <CardBody className="p-5">
                        <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                            <FeatherIcon icon="file-text" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                            Conclusions & Recommendations
                        </div>
                        <div className="p-4 bg-white border rounded">
                            {inspectionData?.conclusions_recommendations || (
                                <span className="text-muted fst-italic">No conclusions or recommendations provided.</span>
                            )}
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Media Viewer */}
            {selectedMedia.url && (
                <MediaViewerModal
                    isOpen={!!selectedMedia.url}
                    toggle={() => setSelectedMedia({ url: '', title: '', type: '' })}
                    mediaUrl={selectedMedia.url}
                    mediaType={selectedMedia.type}
                    title={selectedMedia.title}
                />
            )}
        </>
    );
};

const PreviewBuildingInspection = () => {
    const { token } = useParams();
    const [inspectionData, setInspectionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInspection = async () => {
            try {
                const response = await fetch(
                    `${SERVER_URL}/v2/building-inspections/public`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(response.status === 401 || response.status === 403
                        ? "Invalid or expired preview link"
                        : "Failed to load inspection");
                }

                const result = await response.json();
                setInspectionData(result.data);
            } catch (err) {
                console.error("Preview fetch error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchInspection();
    }, [token]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error || !inspectionData) {
        return (
            <div className="container py-5 text-center">
                <h4 className="text-danger">Error</h4>
                <p>{error || 'Invalid or expired preview link'}</p>
                <Button color="primary" onClick={() => window.location.reload()}>
                    Try Again
                </Button>
            </div>
        );
    }

    return <BuildingInspectionUI inspectionData={inspectionData} token={token} />;
};

export { BuildingInspectionUI, PreviewBuildingInspection };